import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Printer } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const FolioModal = ({ open, onClose, reservationId }) => {
  const [folio, setFolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idBase64, setIdBase64] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (open && reservationId) {
      fetchFolio();
    }
    if (!open) {
      setIdBase64(null);
    }
  }, [open, reservationId]);

  // Convert ID image to base64 for print — fetch as blob to avoid CORS canvas issues
  useEffect(() => {
    if (!folio?.id_image_url) { setIdBase64(null); return; }
    const url = `${BACKEND}${folio.id_image_url}`;
    fetch(url)
      .then(r => r.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => setIdBase64(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => setIdBase64(null));
  }, [folio?.id_image_url]);

  const fetchFolio = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API}/reservations/${reservationId}/folio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolio(res.data);
    } catch (error) {
      console.error('Error fetching folio:', error);
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (d) => {
    try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return d || 'N/A'; }
  };

  const fmtAddr = (obj) => [obj?.address, obj?.city, obj?.state, obj?.zip_code].filter(Boolean).join(', ');

  const handlePrint = () => {
    if (!folio) return;
    const propAddr = fmtAddr(folio.property);
    const guestAddr = fmtAddr(folio.guest);
    const contact = [folio.property.phone, folio.property.email].filter(Boolean).join(' | ');
    const imgSrc = idBase64 || (folio.id_image_url ? `${BACKEND}${folio.id_image_url}` : null);

    let chargesHtml = '';
    (folio.charges || []).forEach(c => {
      chargesHtml += `<tr><td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${c.description}</td><td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:right;">$${c.amount.toFixed(2)}</td></tr>`;
    });

    let paymentsHtml = '';
    (folio.payments || []).forEach(p => {
      paymentsHtml += `<tr><td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${p.payment_method}</td><td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${p.recorded_at ? fmtDate(p.recorded_at) : 'N/A'}</td><td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:right;">$${p.amount.toFixed(2)}</td></tr>`;
    });

    const html = `<!DOCTYPE html><html><head><title>Folio - ${folio.guest.name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#1e293b;padding:20px;max-width:700px;margin:auto}
@page{margin:10mm;size:auto}
@media print{body{padding:10px}}
</style></head><body>

<!-- Header -->
<div style="text-align:center;border-bottom:2px solid #0891b2;padding-bottom:12px;margin-bottom:14px;">
  <div style="font-size:20px;font-weight:bold;color:#0891b2;">${folio.property.name}</div>
  ${propAddr ? `<div style="font-size:11px;color:#64748b;margin-top:2px;">${propAddr}</div>` : ''}
  ${contact ? `<div style="font-size:11px;color:#64748b;">${contact}</div>` : ''}
  <div style="font-size:13px;font-weight:600;color:#334155;margin-top:8px;letter-spacing:1px;">GUEST FOLIO</div>
</div>

<!-- Guest & Stay -->
<table style="width:100%;border-collapse:collapse;margin-bottom:14px;"><tr>
<td style="width:50%;vertical-align:top;padding-right:10px;">
  <div style="font-size:11px;font-weight:bold;color:#0891b2;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;">Guest Information</div>
  <table style="width:100%;font-size:12px;">
    <tr><td style="color:#64748b;padding:2px 0;">Name:</td><td style="font-weight:500;text-align:right;padding:2px 0;">${folio.guest.name}</td></tr>
    ${folio.guest.phone ? `<tr><td style="color:#64748b;padding:2px 0;">Phone:</td><td style="text-align:right;padding:2px 0;">${folio.guest.phone}</td></tr>` : ''}
    ${folio.guest.email ? `<tr><td style="color:#64748b;padding:2px 0;">Email:</td><td style="text-align:right;padding:2px 0;">${folio.guest.email}</td></tr>` : ''}
    ${folio.guest.document_number ? `<tr><td style="color:#64748b;padding:2px 0;">ID:</td><td style="text-align:right;padding:2px 0;">${folio.guest.document_type || ''} ${folio.guest.document_number}</td></tr>` : ''}
    ${guestAddr ? `<tr><td style="color:#64748b;padding:2px 0;">Address:</td><td style="text-align:right;padding:2px 0;max-width:180px;">${guestAddr}</td></tr>` : ''}
  </table>
</td>
<td style="width:50%;vertical-align:top;padding-left:10px;">
  <div style="font-size:11px;font-weight:bold;color:#0891b2;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;">Stay Details</div>
  <table style="width:100%;font-size:12px;">
    <tr><td style="color:#64748b;padding:2px 0;">Room:</td><td style="font-weight:500;text-align:right;padding:2px 0;">${folio.room}</td></tr>
    <tr><td style="color:#64748b;padding:2px 0;">Check-In:</td><td style="text-align:right;padding:2px 0;">${fmtDate(folio.check_in)}</td></tr>
    <tr><td style="color:#64748b;padding:2px 0;">Check-Out:</td><td style="text-align:right;padding:2px 0;">${fmtDate(folio.check_out)}</td></tr>
    <tr><td style="color:#64748b;padding:2px 0;">Nights:</td><td style="text-align:right;padding:2px 0;">${folio.nights}</td></tr>
    <tr><td style="color:#64748b;padding:2px 0;">Guests:</td><td style="text-align:right;padding:2px 0;">${folio.adults} adult${folio.adults > 1 ? 's' : ''}${folio.children > 0 ? `, ${folio.children} child` : ''}</td></tr>
  </table>
</td>
</tr></table>

${imgSrc ? `
<!-- ID Document -->
<div style="margin-bottom:14px;">
  <div style="font-size:11px;font-weight:bold;color:#0891b2;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;">ID Document</div>
  <img src="${imgSrc}" style="max-width:100%;max-height:180px;border:1px solid #e2e8f0;border-radius:4px;" />
</div>` : ''}

<!-- Charges -->
<div style="margin-bottom:14px;">
  <div style="font-size:11px;font-weight:bold;color:#0891b2;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;">Charges</div>
  <table style="width:100%;border-collapse:collapse;font-size:12px;">
    <tr style="background:#f1f5f9;"><th style="text-align:left;padding:6px 8px;font-size:11px;color:#475569;">Description</th><th style="text-align:right;padding:6px 8px;font-size:11px;color:#475569;">Amount</th></tr>
    ${chargesHtml}
    <tr style="font-weight:bold;border-top:2px solid #0891b2;"><td style="padding:6px 8px;">Total</td><td style="padding:6px 8px;text-align:right;">$${folio.total_due.toFixed(2)}</td></tr>
  </table>
</div>

${paymentsHtml ? `
<!-- Payments -->
<div style="margin-bottom:14px;">
  <div style="font-size:11px;font-weight:bold;color:#0891b2;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;">Payments</div>
  <table style="width:100%;border-collapse:collapse;font-size:12px;">
    <tr style="background:#f1f5f9;"><th style="text-align:left;padding:6px 8px;font-size:11px;color:#475569;">Method</th><th style="text-align:left;padding:6px 8px;font-size:11px;color:#475569;">Date</th><th style="text-align:right;padding:6px 8px;font-size:11px;color:#475569;">Amount</th></tr>
    ${paymentsHtml}
  </table>
</div>` : ''}

<!-- Balance -->
<div style="background:#f8fafc;padding:10px 12px;border-radius:6px;font-size:12px;margin-bottom:14px;">
  <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Total Charges:</span><span style="font-weight:500;">$${folio.total_due.toFixed(2)}</span></div>
  <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Total Paid:</span><span style="font-weight:500;color:#15803d;">$${folio.total_paid.toFixed(2)}</span></div>
  <div style="display:flex;justify-content:space-between;font-weight:bold;font-size:14px;border-top:1px solid #cbd5e1;margin-top:4px;padding-top:4px;"><span>Balance Due:</span><span style="color:${folio.balance > 0 ? '#dc2626' : '#16a34a'};">$${folio.balance.toFixed(2)}</span></div>
</div>

${(folio.notes || folio.guest_note) ? `
<!-- Notes -->
<div style="margin-bottom:14px;">
  <div style="font-size:11px;font-weight:bold;color:#0891b2;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;">Notes</div>
  ${folio.notes ? `<div style="font-size:12px;color:#334155;padding:4px 0;">${folio.notes}</div>` : ''}
  ${folio.guest_note ? `<div style="font-size:12px;color:#334155;padding:4px 0;"><span style="color:#64748b;">Guest note:</span> ${folio.guest_note}</div>` : ''}
</div>` : ''}

<!-- Footer -->
<div style="text-align:center;border-top:1px solid #e2e8f0;padding-top:12px;font-size:10px;color:#94a3b8;">
  <div>Thank you for choosing ${folio.property.name}!</div>
  <div>Folio generated on ${format(new Date(), 'MMM dd, yyyy h:mm a')}</div>
</div>

</body></html>`;

    const pw = window.open('', '_blank');
    pw.document.write(html);
    pw.document.close();
    // Wait for images to load before printing
    if (imgSrc) {
      const imgs = pw.document.querySelectorAll('img');
      let loaded = 0;
      const tryPrint = () => { loaded++; if (loaded >= imgs.length) setTimeout(() => pw.print(), 200); };
      imgs.forEach(i => { if (i.complete) tryPrint(); else { i.onload = tryPrint; i.onerror = tryPrint; } });
      // Fallback
      setTimeout(() => pw.print(), 2000);
    } else {
      setTimeout(() => pw.print(), 300);
    }
  };

  if (!open) return null;

  const imgUrl = (folio?.id_image_url && folio.id_image_url.length > 1) ? `${BACKEND}${folio.id_image_url}` : null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="folio-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Guest Folio</span>
            <Button size="sm" variant="outline" onClick={handlePrint} disabled={!folio} data-testid="folio-print-btn">
              <Printer size={14} className="mr-1" /> Print
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading folio...</div>
        ) : folio ? (
          <div ref={printRef} data-testid="folio-content">
            {/* Header */}
            <div style={{textAlign:'center',borderBottom:'2px solid #0891b2',paddingBottom:12,marginBottom:14}}>
              <div style={{fontSize:20,fontWeight:'bold',color:'#0891b2'}}>{folio.property.name}</div>
              {fmtAddr(folio.property) && <div style={{fontSize:11,color:'#64748b',marginTop:2}}>{fmtAddr(folio.property)}</div>}
              <div style={{fontSize:11,color:'#64748b'}}>{[folio.property.phone, folio.property.email].filter(Boolean).join(' | ')}</div>
              <div style={{fontSize:13,fontWeight:600,color:'#334155',marginTop:8,letterSpacing:1}}>GUEST FOLIO</div>
            </div>

            {/* Guest & Stay */}
            <div style={{display:'flex',gap:16,marginBottom:14,fontSize:12}}>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:'bold',color:'#0891b2',borderBottom:'1px solid #e2e8f0',paddingBottom:3,marginBottom:6}}>Guest Information</div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Name:</span><span style={{fontWeight:500}}>{folio.guest.name}</span></div>
                {folio.guest.phone && <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Phone:</span><span>{folio.guest.phone}</span></div>}
                {folio.guest.email && <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Email:</span><span>{folio.guest.email}</span></div>}
                {folio.guest.document_number && <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>ID:</span><span>{folio.guest.document_type} {folio.guest.document_number}</span></div>}
                {fmtAddr(folio.guest) && <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Address:</span><span style={{textAlign:'right',maxWidth:200}}>{fmtAddr(folio.guest)}</span></div>}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:'bold',color:'#0891b2',borderBottom:'1px solid #e2e8f0',paddingBottom:3,marginBottom:6}}>Stay Details</div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Room:</span><span style={{fontWeight:500}}>{folio.room}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Check-In:</span><span>{fmtDate(folio.check_in)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Check-Out:</span><span>{fmtDate(folio.check_out)}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Nights:</span><span>{folio.nights}</span></div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span style={{color:'#64748b'}}>Guests:</span><span>{folio.adults} adult{folio.adults > 1 ? 's' : ''}{folio.children > 0 ? `, ${folio.children} child` : ''}</span></div>
              </div>
            </div>

            {/* ID Image */}
            {imgUrl && (
              <div style={{marginBottom:14}} data-testid="folio-id-image-section">
                <div style={{fontSize:11,fontWeight:'bold',color:'#0891b2',borderBottom:'1px solid #e2e8f0',paddingBottom:3,marginBottom:6}}>ID Document</div>
                <img src={imgUrl} alt="Guest ID" style={{maxWidth:'100%',maxHeight:180,border:'1px solid #e2e8f0',borderRadius:4}} onError={(e) => { e.target.style.display='none'; }} />
              </div>
            )}

            {/* Charges */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:'bold',color:'#0891b2',borderBottom:'1px solid #e2e8f0',paddingBottom:3,marginBottom:6}}>Charges</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#f1f5f9'}}>
                    <th style={{textAlign:'left',padding:'6px 8px',fontSize:11,color:'#475569'}}>Description</th>
                    <th style={{textAlign:'right',padding:'6px 8px',fontSize:11,color:'#475569'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {folio.charges.map((c, i) => (
                    <tr key={i}><td style={{padding:'6px 8px',borderBottom:'1px solid #f1f5f9'}}>{c.description}</td><td style={{padding:'6px 8px',borderBottom:'1px solid #f1f5f9',textAlign:'right'}}>${c.amount.toFixed(2)}</td></tr>
                  ))}
                  <tr style={{fontWeight:'bold',borderTop:'2px solid #0891b2'}}><td style={{padding:'6px 8px'}}>Total</td><td style={{padding:'6px 8px',textAlign:'right'}}>${folio.total_due.toFixed(2)}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Payments */}
            {folio.payments?.length > 0 && (
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:'bold',color:'#0891b2',borderBottom:'1px solid #e2e8f0',paddingBottom:3,marginBottom:6}}>Payments</div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#f1f5f9'}}>
                      <th style={{textAlign:'left',padding:'6px 8px',fontSize:11,color:'#475569'}}>Method</th>
                      <th style={{textAlign:'left',padding:'6px 8px',fontSize:11,color:'#475569'}}>Date</th>
                      <th style={{textAlign:'right',padding:'6px 8px',fontSize:11,color:'#475569'}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {folio.payments.map((p, i) => (
                      <tr key={i}><td style={{padding:'6px 8px',borderBottom:'1px solid #f1f5f9'}}>{p.payment_method}</td><td style={{padding:'6px 8px',borderBottom:'1px solid #f1f5f9'}}>{p.recorded_at ? fmtDate(p.recorded_at) : 'N/A'}</td><td style={{padding:'6px 8px',borderBottom:'1px solid #f1f5f9',textAlign:'right'}}>${p.amount.toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Balance */}
            <div style={{background:'#f8fafc',padding:'10px 12px',borderRadius:6,fontSize:12,marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span>Total Charges:</span><span style={{fontWeight:500}}>${folio.total_due.toFixed(2)}</span></div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0'}}><span>Total Paid:</span><span style={{fontWeight:500,color:'#15803d'}}>${folio.total_paid.toFixed(2)}</span></div>
              <div style={{display:'flex',justifyContent:'space-between',fontWeight:'bold',fontSize:14,borderTop:'1px solid #cbd5e1',marginTop:4,paddingTop:4}}><span>Balance Due:</span><span style={{color:folio.balance > 0 ? '#dc2626' : '#16a34a'}}>${folio.balance.toFixed(2)}</span></div>
            </div>

            {/* Notes */}
            {(folio.notes || folio.guest_note) && (
              <div style={{marginBottom:14}} data-testid="folio-notes-section">
                <div style={{fontSize:11,fontWeight:'bold',color:'#0891b2',borderBottom:'1px solid #e2e8f0',paddingBottom:3,marginBottom:6}}>Notes</div>
                {folio.notes && <div style={{fontSize:12,color:'#334155',padding:'4px 0'}}>{folio.notes}</div>}
                {folio.guest_note && <div style={{fontSize:12,color:'#334155',padding:'4px 0'}}><span style={{color:'#64748b'}}>Guest note:</span> {folio.guest_note}</div>}
              </div>
            )}

            {/* Footer */}
            <div style={{textAlign:'center',borderTop:'1px solid #e2e8f0',paddingTop:12,fontSize:10,color:'#94a3b8'}}>
              <div>Thank you for choosing {folio.property.name}!</div>
              <div>Folio generated on {format(new Date(), 'MMM dd, yyyy h:mm a')}</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">No folio data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FolioModal;
